import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Tooltip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";

import { getSuppliers, deleteSupplier, searchSuppliersByName } from '../services/supplierService';
import ConfirmationDialog from '../../../components/ConfirmationDialog';

const ListSupplier = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null, name: '' });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const searchDebounceRef = useRef(null);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetchSuppliers();
  }, []);

  useEffect(() => {
    if (!searchTerm.trim()) return undefined;

    searchDebounceRef.current = setTimeout(() => {
      handleSearch(searchTerm);
    }, 500);

    return () => clearTimeout(searchDebounceRef.current);
  }, [searchTerm]);

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await getSuppliers({ suppressErrorToast: true });
      
      let supplierData = [];
      if (response?.payload && Array.isArray(response.payload)) {
        supplierData = response.payload;
      } else if (Array.isArray(response)) {
        supplierData = response;
      } else if (response?.data && Array.isArray(response.data)) {
        supplierData = response.data;
      }
      
      setSuppliers(supplierData);
      
    } catch (error) {

      if (error.response && error.response.status !== 404) {
        setError('Failed to load suppliers. Please try again.');
      } else {
        // Empty result is not an error
        setSuppliers([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (term = searchTerm) => {
    if (!term.trim()) {
      fetchSuppliers();
      return;
    }

    try {
      setSearchLoading(true);
      
      const response = await searchSuppliersByName(term.trim(), { suppressErrorToast: true });
      
      let searchResults = [];
      if (response?.payload && Array.isArray(response.payload)) {
        searchResults = response.payload;
      } else if (Array.isArray(response)) {
        searchResults = response;
      } else if (response?.data && Array.isArray(response.data)) {
        searchResults = response.data;
      }
      
      setSuppliers(searchResults);
      setPage(0); // Reset to first page after search
      
    } catch (error) {

      setError('Search failed. Please try again.');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleDelete = async (supplierId, supplierName) => {
    setDeleteLoading(true);
    try {
      await deleteSupplier(supplierId, { suppressErrorToast: true });
      
      const updatedSuppliers = suppliers.filter(supplier => supplier.Id !== supplierId);
      setSuppliers(updatedSuppliers);
      
      // Reset page if current page would be empty after deletion
      const newSupplierCount = updatedSuppliers.length;
      const maxPage = Math.ceil(newSupplierCount / rowsPerPage) - 1;
      if (page > maxPage && maxPage >= 0) {
        setPage(maxPage);
      }
      
    } catch (error) {
      setError('Failed to delete supplier');
    } finally {
      setDeleteLoading(false);
      setDeleteDialog({ open: false, id: null, name: '' });
    }
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    
    if (!value.trim()) {
      fetchSuppliers();
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      clearTimeout(searchDebounceRef.current);
      handleSearch();
    }
  };

  const handleAddSupplier = () => {
    navigate('/suppliers/add');
  };

  const handleEditSupplier = (supplierId) => {
    navigate(`/suppliers/edit/${supplierId}`);
  };

  const handleViewSupplier = (supplierId) => {
    navigate(`/suppliers/view/${supplierId}`);
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusChip = (isActive) => {
    return (
      <Chip
        label={isActive ? t("active") : t("inactive")}
        color={isActive ? 'success' : 'default'}
        size="small"
        variant="outlined"
      />
    );
  };

  const filteredSuppliers = suppliers.filter(supplier => {
    const matchesFilter =
      filterType === 'all' ||
      (filterType === 'active' && supplier.IsActive !== false) ||
      (filterType === 'inactive' && supplier.IsActive === false);
    return matchesFilter;
  });

  // Calculate paginated suppliers
  const paginatedSuppliers = filteredSuppliers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 1, sm: 2 }, mb: 4, px: { xs: 1, sm: 2, md: 3 }, width: '100%', minWidth: 0 }}>
      {/* ✅ Header Section */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' },
        flexDirection: { xs: 'column', sm: 'row' },
        mb: { xs: 2, sm: 3 },
        gap: 2
      }}>
        <Box>
          <Typography 
            variant="h4" 
            component="h1" 
            sx={{ 
              fontWeight: 600,
              color: 'primary.main',
              mb: 0.5,
              fontSize: { xs: '1.75rem', sm: '2.125rem' }
            }}
          >
            {t("suppliers")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("manage_supplier_info")}
          </Typography>
        </Box>
        
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddSupplier}
          sx={{ 
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
            width: { xs: '100%', sm: 'auto' }
          }}
        >
          {t("add_supplier")}
        </Button>
      </Box>

      {/* ✅ Search and Actions Section */}
      <Paper
        elevation={2}
        sx={{
          p: { xs: 1.5, sm: 3 },
          mb: 3,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder={t("search_suppliers_placeholder")}
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyPress={handleKeyPress}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchTerm('');
                        fetchSuppliers();
                      }}
                      edge="end"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: 'background.paper'
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>{t("filter")}</InputLabel>
              <Select
                value={filterType}
                label={t("filter")}
                onChange={(e) => setFilterType(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="all">{t("all_suppliers")}</MenuItem>
                <MenuItem value="active">{t("active_only")}</MenuItem>
                <MenuItem value="inactive">{t("inactive_only")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Chip
                label={`${filteredSuppliers.length} ${t("suppliers")}`}
                color="primary"
                variant="outlined"
                icon={<FilterListIcon />}
              />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* ✅ Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* ✅ Suppliers Table */}
      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {loading || searchLoading ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            py: 8 
          }}>
            <CircularProgress size={40} />
            <Typography variant="body1" sx={{ ml: 2 }}>
              {t("loading_suppliers")}
            </Typography>
          </Box>
        ) : filteredSuppliers.length === 0 ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center', 
            alignItems: 'center', 
            py: 8,
            textAlign: 'center'
          }}>
            <BusinessIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {searchTerm || filterType !== 'all' ? t("no_suppliers_found") : t("no_suppliers_yet")}
            </Typography>
            <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
              {searchTerm || filterType !== 'all'
                ? t("try_adjusting_search_criteria") 
                : t("start_by_adding_first_supplier")
              }
            </Typography>
            {!searchTerm && filterType === 'all' && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddSupplier}
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                {t("add_first_supplier")}
              </Button>
            )}
          </Box>
        ) : (
          <>
            <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600, px: { xs: 1, sm: 2 } }}>{t("supplier_name")}</TableCell>
                    <TableCell sx={{ fontWeight: 600, display: { xs: 'none', sm: 'table-cell' } }}>{t("contact_info")}</TableCell>
                    <TableCell sx={{ fontWeight: 600, display: { xs: 'none', md: 'table-cell' } }}>{t("address")}</TableCell>
                    <TableCell sx={{ fontWeight: 600, px: { xs: 1, sm: 2 } }}>{t("status")}</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, px: { xs: 0.5, sm: 2 } }}>{t("actions")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedSuppliers.map((supplier, index) => (
                    <TableRow 
                      key={supplier.Id || index}
                      hover
                      sx={{ 
                        '&:hover': { backgroundColor: 'action.hover' },
                        borderBottom: '1px solid',
                        borderColor: 'divider'
                      }}
                    >
                      <TableCell sx={{ px: { xs: 1, sm: 2 }, maxWidth: { xs: 170, sm: 'none' } }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body1" sx={{ fontWeight: 500, overflowWrap: 'anywhere' }}>
                            {supplier.Name || supplier.SupplierName || 'N/A'}
                          </Typography>
                          {supplier.CompanyRegistrationNumber && (
                            <Typography variant="caption" color="text.secondary">
                              {t("registration_number")}: {supplier.CompanyRegistrationNumber}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>

                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Box>
                          {supplier.Phone && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                              <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="body2">{supplier.Phone}</Typography>
                            </Box>
                          )}
                          {supplier.Email && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="body2">{supplier.Email}</Typography>
                            </Box>
                          )}
                          {!supplier.Phone && !supplier.Email && (
                            <Typography variant="body2" color="text.disabled">
                              {t("no_contact_info")}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>

                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Box>
                          {(supplier.Address || supplier.City || supplier.Country) ? (
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                              <LocationIcon sx={{ fontSize: 14, color: 'text.secondary', mt: 0.2 }} />
                              <Box>
                                {supplier.Address && (
                                  <Typography variant="body2">{supplier.Address}</Typography>
                                )}
                                {(supplier.City || supplier.Country) && (
                                  <Typography variant="caption" color="text.secondary">
                                    {[supplier.City, supplier.Country].filter(Boolean).join(', ')}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.disabled">
                              {t("no_address")}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>

                      <TableCell sx={{ px: { xs: 1, sm: 2 } }}>
                        {getStatusChip(supplier.IsActive !== false)}
                      </TableCell>

                      <TableCell align="center" sx={{ px: { xs: 0.5, sm: 2 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: { xs: 0, sm: 0.5 } }}>
                          <Tooltip title={t("view_details")}>
                            <IconButton
                              size="small"
                              onClick={() => handleViewSupplier(supplier.Id)}
                              sx={{ color: 'info.main' }}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title={t("edit_supplier")}>
                            <IconButton
                              size="small"
                              onClick={() => handleEditSupplier(supplier.Id)}
                              sx={{ color: 'warning.main' }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title={t("delete_supplier")}>
                            <IconButton
                              size="small"
                              onClick={() => setDeleteDialog({ open: true, id: supplier.Id, name: supplier.Name || supplier.SupplierName })}
                              sx={{ color: 'error.main' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination Component */}
            <TablePagination
              component="div"
              count={filteredSuppliers.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: 'grey.50'
              }}
            />
          </>
        )}
      </Paper>
      <ConfirmationDialog
        open={deleteDialog.open}
        title={t("delete_supplier")}
        message={t("confirm_delete_supplier", { supplier: deleteDialog.name }) || `Are you sure you want to delete "${deleteDialog.name}"?`}
        confirmText={t("delete_supplier")}
        cancelText={t("cancel")}
        variant="danger"
        loading={deleteLoading}
        onCancel={() => !deleteLoading && setDeleteDialog({ open: false, id: null, name: '' })}
        onConfirm={() => handleDelete(deleteDialog.id, deleteDialog.name)}
      />
    </Container>
  );
};

export default ListSupplier;